import Script from "next/script"
import cn from "classnames"
import CallToAction from "components/CallToAction"
import Image from "components/Image"
import { getStoryblokLink } from "utilities/getStoryblokLink"
import { getExcerpt } from "utilities/getExcerpt"
import richText from "utilities/richText"

const DateBox = ({ dateObj, isImagePresent }) => {
  return (
    <div
      className={cn(
        "absolute left-0 top-0 z-10 flex h-[60px] w-[60px] flex-col justify-center bg-orange text-center font-secondary text-m2 font-medium text-white md:static md:h-20 md:w-20 md:text-l2",
        { "hidden md:flex": !isImagePresent },
      )}
    >
      <div className="uppercase leading-5 md:leading-7">{dateObj.toLocaleString("en-us", { month: "short" })}</div>
      <div className="leading-5 md:leading-7">{dateObj.getDate()}</div>
    </div>
  )
}

const Event = ({
  event: { date, date_details, title, location_name, address_link_text, address_link, description, image },
}) => {
  const dateObj = new Date(date)
  const isImagePresent = !!image.id
  const dateDetails = date_details.split("|")

  return (
    <>
      <div className="relative mx-auto mt-7 grid max-w-5xl gap-x-7 md:grid-cols-[80px_1fr_1fr]">
        {date && <DateBox dateObj={dateObj} isImagePresent={isImagePresent} />}

        {image.id && (
          <div className="mb-7 md:order-last">
            <Image
              src={image?.filename}
              alt={image?.alt}
              placeholder={image?.blurDataURL ? "blur" : "empty"}
              blurDataURL={image?.blurDataURL}
              width={387}
              height={205}
            />
          </div>
        )}

        <div>
          <div className="font-secondary uppercase">
            {dateDetails.length > 1 ? (
              <>
                {dateDetails[0]}
                <span className="px-2 text-orange">{"|"}</span>
                {dateDetails[1]}
              </>
            ) : (
              <>{dateDetails[0]}</>
            )}
          </div>
          <h3 className="py-1.5 text-teal-dark">{title}</h3>
          <span className="block font-tertiary uppercase md:inline-block">{location_name}</span>
          <CallToAction style="inline" href={getStoryblokLink(address_link)}>
            <br />
            <span className="font-tertiary font-medium uppercase text-teal-dark">{address_link_text}</span>
          </CallToAction>

          <div className="max-w-lg pt-5">{richText(description)}</div>
        </div>
      </div>
      <Script type="application/ld+json" id="event-schema">
        {`{
         "@context": "https://schema.org",
         "@type": "Event",
         "name": ${title},
         "startDate": ${dateObj.toLocaleDateString("en-US")},
         "endDate": ${dateObj.toLocaleDateString("en-US")},
         "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
         "eventStatus": "https://schema.org/EventScheduled",
         "location": {
           "@type": "Place",
           "name": ${location_name},
           "address": ${address_link_text},
           }
         },
         "image": ${image.filename},
         "description": ${getExcerpt(description)},
         "organizer": {
           "@type": "Organization",
           "name": "CHNK",
           "url": "https://www.chnk.org/"
      }`}
      </Script>
    </>
  )
}

export default function EventSection({ blok }) {
  if (!blok?.events?.length) return null

  return (
    <section className={cn("px-6", { "bg-stone-mid": blok.is_featured_event })}>
      <div className="mx-auto max-w-screen-xl py-7 lg:py-12">
        <h2 className="mx-auto max-w-5xl font-medium text-teal-dark">{blok.heading}</h2>
        {blok.events.map((event) => (
          <Event key={event._uid} event={event} />
        ))}
      </div>
    </section>
  )
}
